// Utility functions for calculating distances between coordinates
import { LOGGING } from '/settings.js';

/**
 * Returns the distance in meters between two lat/lng points using the Haversine formula.
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number} Distance in meters
 */
export function getDistanceMeters(lat1, lng1, lat2, lng2) {
    const R = 6371000; // Earth radius in meters
    const toRad = deg => deg * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Formats a distance in meters as a string (e.g., '850 m' or '2.1 km').
 * @param {number} meters
 * @returns {string}
 */
export function formatDistance(meters) {
    if (meters < 1000) {
        return `${Math.round(meters)} m.`;
    } else {
        return `${(meters / 1000).toFixed(1)} km.`;
    }
}

/**
 * Requests the user's location using the browser API, stores it, and returns a Promise.
 * @returns {Promise<{ latitude: number, longitude: number }>} Resolves to the location object
 */
export function getAndStoreUserLocation() {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            if (LOGGING.ENABLED && LOGGING.GENERAL) {
                console.error('[location-utils] Geolocation is not supported by your browser');
            }
            reject(new Error('Geolocation not supported'));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            pos => {
                const { latitude, longitude } = pos.coords;
                if (typeof window !== 'undefined') {
                    window.userLocation = { latitude, longitude };
                    try {
                        sessionStorage.setItem('userLocation', JSON.stringify({ latitude, longitude }));
                    } catch (e) { }
                }
                if (LOGGING.ENABLED && LOGGING.GENERAL) {
                    console.log('[location-utils] Got user location:', { latitude, longitude });
                }
                resolve({ latitude, longitude });
            },
            err => {
                if (LOGGING.ENABLED && LOGGING.GENERAL) {
                    console.error('[location-utils] Error getting location:', err);
                }
                reject(err);
            },
            {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0
            }
        );
    });
}

/**
 * Retrieves the user's location from sessionStorage or window.userLocation
 * @returns {{ latitude: number, longitude: number } | null}
 */
export function getUserLocation() {
    if (typeof window !== 'undefined') {
        if (window.userLocation && typeof window.userLocation.latitude === 'number' && typeof window.userLocation.longitude === 'number') {
            return window.userLocation;
        }
        try {
            const stored = sessionStorage.getItem('userLocation');
            if (stored) {
                const obj = JSON.parse(stored);
                if (typeof obj.latitude === 'number' && typeof obj.longitude === 'number') {
                    window.userLocation = obj;
                    return obj;
                }
            }
        } catch (e) { }
    }
    return null;
}

/**
 * Creates and returns a Leaflet marker for the user's location (blue circle).
 * @param {Array|Object} latLng - The latitude/longitude array or object
 * @returns {L.Marker}
 */
export function showUserLocation(latLng) {
    return L.marker(latLng, {
        icon: L.divIcon({
            className: LOCATION_MARKER_SETTINGS.CLASS_NAME,
            html: LOCATION_MARKER_SETTINGS.ICON_HTML,
            iconSize: LOCATION_MARKER_SETTINGS.ICON_SIZE,
            iconAnchor: LOCATION_MARKER_SETTINGS.ICON_ANCHOR,
            popupAnchor: LOCATION_MARKER_SETTINGS.POPUP_ANCHOR
        })
    });
} 